import styled from '@emotion/styled'
import { useEffect } from 'react'
import { InputContainer, InputContainerProps } from './InputContainer'
import { useId, useCallback, useState } from 'react'
// import { getErrorStyles, getInputStyles } from 'css/utils'
import { errorStyles, inputStyles } from 'css/utils'
import { RefCallBack } from 'react-hook-form'
import { UiIcon } from 'modules/ui/atoms/Icon'

// TODO: Likely future work - getting theme from org settings
// import useThemeHook from 'utils/hooks/theme'
import { useTranslation } from 'next-i18next'

export type InputProps = Omit<InputContainerProps, 'id'> & {
  id?: string
  dataId?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void
  onBlur?: () => void
  onFocus?: () => void
  onKeyDown?: (e: any) => void
  inputRef?: RefCallBack
  max?: number
  min?: number
  autoComplete?: string
  leftIcon?: string
  rightIcon?: string
  inputMode?:
    | 'text'
    | 'search'
    | 'none'
    | 'email'
    | 'tel'
    | 'url'
    | 'numeric'
    | 'decimal'
    | undefined
  tabIndex?: number | undefined
  overwriteValueWithFocus?: boolean
  hasCounter?: boolean
  autoFocus?: boolean
}

type StyledInputProps = {
  errored?: boolean
  leftIcon?: string
  rightIcon?: string
}

const StyledInput = styled.input<StyledInputProps>(
  () => {
    // const theme = useThemeHook()

    return {
      minHeight: '40px',
      ...inputStyles, //...getInputStyles(theme),
    }
  },
  ({ leftIcon }) => {
    if (leftIcon) {
      return { paddingLeft: '40px' }
    }

    return {}
  },
  ({ rightIcon }) => {
    if (rightIcon) {
      return { paddingLeft: '40px' }
    }

    return {}
  },
  errorStyles,
)

const StyledStartIcon = styled.i<StyledInputProps>({
  position: 'absolute',
  padding: '14px 12px',
})

const StyledEndIcon = styled.i<StyledInputProps>({
  position: 'absolute',
  padding: '12px',
  marginLeft: '-48px',
})

export const Input = ({
  caption,
  captionId: inheritedCaptionId,
  id: inputId,
  dataId = '',
  onChange,
  required,
  onBlur,
  onFocus,
  onKeyDown,
  inputRef,
  value,
  label,
  placeholder,
  maxLength,
  max,
  min,
  error,
  type,
  inputMode,
  leftIcon,
  rightIcon,
  tabIndex,
  overwriteValueWithFocus = false,
  hasOverflow = true,
  autoComplete,
  hideErrorCaption,
  hideLabel,
  hasCounter = true,
  autoFocus,
}: InputProps) => {
  const id = useId()

  const ownCaptionId = useId()
  const captionId = inheritedCaptionId || ownCaptionId

  const { t } = useTranslation()

  const [inputValue, setInputValue] = useState(value)
  const [hasFocus, setHasFocus] = useState(false)

  const maxLengthError = maxLength && inputValue && inputValue.length > maxLength
  let maxLengthErrorText = ''

  if (maxLengthError) {
    const extraCharacters = inputValue.length - maxLength

    maxLengthErrorText = t('Input.maxLengthErrorText', {
      defaultValue: `You are ${extraCharacters} character${
        extraCharacters > 1 ? 's' : ''
      } over the limit.`,
      extraCharacters,
    })
  }

  useEffect(() => {
    if (hasFocus && !overwriteValueWithFocus) {
      return
    }

    setInputValue(value)
  }, [value])

  const onChangeCallback = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
      onChange(e.target.value)
    },
    [onChange],
  )

  const onClearClick = () => {
    setInputValue('')
    onChange('')
  }

  const handleOnBlur = () => {
    setHasFocus(false)
    onChange(value?.trim())
    if (onBlur) onBlur()
  }

  const handleOnFocus = () => {
    setHasFocus(true)

    if (onFocus) onFocus()
  }

  return (
    <InputContainer
      caption={caption}
      captionId={captionId}
      error={maxLengthError ? maxLengthErrorText : error}
      hasCounter={hasCounter}
      hasOverflow={true}
      hideErrorCaption={hideErrorCaption}
      hideLabel={hideLabel}
      id={id}
      label={label}
      maxLength={maxLength}
      required={required}
      value={value}
    >
      {leftIcon && (
        <StyledStartIcon>
          <UiIcon name={leftIcon} w={24} h={24} />
        </StyledStartIcon>
      )}
      <StyledInput
        data-id={dataId}
        id={inputId ? inputId : id}
        type={type}
        ref={inputRef}
        onChange={onChangeCallback}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        onKeyDown={onKeyDown}
        value={inputValue}
        placeholder={placeholder}
        errored={!!error || !!maxLengthError}
        leftIcon={leftIcon}
        max={max}
        min={min}
        inputMode={inputMode}
        aria-describedby={captionId}
        aria-errormessage={!!error ? captionId : undefined}
        aria-invalid={!!error ? 'true' : 'false'}
        aria-label={label}
        tabIndex={tabIndex}
        maxLength={hasOverflow ? -1 : maxLength}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
      />
      {rightIcon && inputValue && (
        <StyledEndIcon onClick={onClearClick}>
          <UiIcon name={rightIcon} w={24} h={24} />
        </StyledEndIcon>
      )}
    </InputContainer>
  )
}
